/**
 * Pure auth helpers: mode selection, token choice, callback detection, /api/me parsing, 401 retry.
 * No browser globals and no oidc-client-ts import here, so everything is unit-testable in node.
 */

import { MeResponse, type AuthMode, type Role } from '@idrp/shared';

export type { AuthMode, MeResponse, Role };

export interface OidcSettings {
  authority: string;
  clientId: string;
  scope: string;
}

type Env = Record<string, string | boolean | undefined>;
const str = (v: string | boolean | undefined) => (typeof v === 'string' ? v.trim() : '');

/** OIDC is on only when `VITE_OIDC_AUTHORITY` is a non-empty string. Unset or empty = static dev tokens. */
export function readOidcSettings(env: Env): OidcSettings | null {
  const authority = str(env.VITE_OIDC_AUTHORITY).replace(/\/+$/, '');
  if (!authority) return null;
  return {
    authority,
    clientId: str(env.VITE_OIDC_CLIENT_ID) || 'idrp-web',
    scope: str(env.VITE_OIDC_SCOPE) || 'openid profile email',
  };
}

export const modeOf = (settings: OidcSettings | null): AuthMode => (settings ? 'oidc' : 'static');

/** Static mode: the dev token of the selected role. OIDC mode: the access token, or null when there is none. */
export function pickToken(
  mode: AuthMode,
  role: Role,
  staticTokens: Record<Role, string>,
  oidcAccessToken: string | null | undefined,
): string | null {
  if (mode === 'static') return staticTokens[role];
  return oidcAccessToken || null;
}

/** The provider redirected back to us: `?code&state` on success, `?error&state` on failure. */
export function isSigninCallback(search: string): boolean {
  const p = new URLSearchParams(search);
  return p.has('state') && (p.has('code') || p.has('error'));
}

const AUTH_PARAMS = ['code', 'state', 'session_state', 'error', 'error_description', 'error_uri', 'iss'];

/** The current URL without the authorization response parameters, used as the post-login return path. */
export function stripAuthParams(pathname: string, search: string, hash = ''): string {
  const p = new URLSearchParams(search);
  for (const k of AUTH_PARAMS) p.delete(k);
  const q = p.toString();
  return `${pathname}${q ? `?${q}` : ''}${hash}`;
}

/** Validate `/api/me`. Anything that is not exactly `supervisor` is treated as `viewer` (least privilege). */
export function parseMe(body: unknown): MeResponse | null {
  if (!body || typeof body !== 'object') return null;
  const user = MeResponse.shape.user.safeParse((body as { user?: unknown }).user);
  if (!user.success) return null;
  const role = MeResponse.shape.role.safeParse((body as { role?: unknown }).role);
  return { user: user.data, role: role.success && role.data === 'supervisor' ? 'supervisor' : 'viewer' };
}

export interface RetryDeps<R extends { status: number }> {
  /** One HTTP attempt with whatever token is current. */
  attempt: () => Promise<R>;
  /** Silent renew or refresh. Resolves true when a new token is available. */
  renew: () => Promise<boolean>;
  /** Start an interactive sign-in (leaves the page). */
  signIn: () => void;
}

/**
 * OIDC 401 handling: retry once after a silent renew. If renew fails or the retry is still 401, send the user to sign
 * in. 403 is returned as is (signed in, but not a supervisor).
 */
export async function withAuthRetry<R extends { status: number }>(deps: RetryDeps<R>): Promise<R> {
  const first = await deps.attempt();
  if (first.status !== 401) return first;
  if (!(await deps.renew())) {
    deps.signIn();
    return first;
  }
  const second = await deps.attempt();
  if (second.status === 401) deps.signIn();
  return second;
}
